import { zMfaChallenge, zPasswordResetRequired } from "@platform/api/auth";

import type { z } from "zod";

// A login waiting on an MFA code or a password reset. It's kept in localStorage when the
// user chose to stay signed in and in sessionStorage otherwise, so a reload mid-step
// still knows that choice. onboardingError.js reads the same key.
const PENDING_LOGIN_KEY = "mfaState";

const pendingLoginStepSchema = zMfaChallenge.or(zPasswordResetRequired);

export type PendingLoginStep = z.infer<typeof pendingLoginStepSchema>;

export interface PendingLogin {
  readonly step: PendingLoginStep;
  readonly keepSignedIn: boolean;
}

const parseStep = (raw: string | null): PendingLoginStep | undefined => {
  if (!raw) return undefined;
  try {
    const parsed = pendingLoginStepSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    // Unreadable JSON is treated as no pending login.
    return undefined;
  }
};

export const readPendingLogin = (): PendingLogin | null => {
  const kept = parseStep(localStorage.getItem(PENDING_LOGIN_KEY));
  if (kept) return { step: kept, keepSignedIn: true };
  const sessionOnly = parseStep(sessionStorage.getItem(PENDING_LOGIN_KEY));
  return sessionOnly ? { step: sessionOnly, keepSignedIn: false } : null;
};

export const storePendingLogin = ({
  step,
  keepSignedIn,
}: PendingLogin): void => {
  const storage = keepSignedIn ? localStorage : sessionStorage;
  storage.setItem(PENDING_LOGIN_KEY, JSON.stringify(step));
};

export const clearPendingLogin = (): void => {
  localStorage.removeItem(PENDING_LOGIN_KEY);
  sessionStorage.removeItem(PENDING_LOGIN_KEY);
};
